/*
 * THE HOUSE OF JK — Compare tray + comparison table
 * Ported from JSX lines 1380-1401 (CompareBar) and 1402-1455 (CompareModal),
 * plus the toggle/limit logic at JSX 2496-2510.
 *
 * Drives snippets/compare-bar.liquid, snippets/compare-modal.liquid and the
 * [data-compare-toggle] button inside snippets/product-card.liquid.
 *
 * DELIBERATE DEVIATION FROM THE SOURCE — persistence.
 * The React prototype held compareList in component state, so it was lost on
 * reload. This is now a multi-page storefront: a shopper picks one fragrance on
 * /collections/for-him, navigates, and picks the next. Without persistence the
 * feature cannot work at all, so the selection is stored in sessionStorage
 * under 'jk-compare'. sessionStorage (not localStorage) keeps it scoped to the
 * browsing session, which matches the throwaway feel of the original state.
 *
 * Everything else is faithful: cap of 3 (JSX 2499), the 2s over-limit notice
 * (JSX 2502), open disabled below 2 (JSX 1394), the count suffix only from 2
 * upward (JSX 1395), and the six table rows in the original order
 * (JSX 1404-1409 + 1441).
 *
 * All user-visible strings arrive from Liquid as data-label-* attributes.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'jk-compare';
  var MAX = 3;          // JSX 2499
  var MIN_TO_OPEN = 2;  // JSX 1394
  var NOTICE_MS = 2000; // JSX 2502

  /* ---------------------------------------------------------------- utils */

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function attr(el, name) {
    if (!el) return '';
    var v = el.getAttribute(name);
    return v == null ? '' : v;
  }

  function asList(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value !== '') return [value];
    return [];
  }

  function isRtl() {
    return !!(window.jkSettings && window.jkSettings.isRtl);
  }

  // JSX 48: arList joins with ", " in English and "، " in Arabic.
  function joinList(list) {
    return list.join(isRtl() ? '، ' : ', ');
  }

  function rootPath() {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return String(cartUrl).replace(/\/cart\/?$/, '');
  }

  // Standard Shopify money formatting, used only when the inline catalog did
  // not already hand us a | money formatted string.
  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var format = window.moneyFormat || '{{amount}}';

    function num(precision, thousands, decimal) {
      var value = (cents / 100.0).toFixed(precision);
      var parts = value.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      switch (name) {
        case 'amount': return num(2, ',', '.');
        case 'amount_no_decimals': return num(0, ',', '.');
        case 'amount_with_comma_separator': return num(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return num(0, '.', ',');
        case 'amount_with_apostrophe_separator': return num(2, "'", '.');
        case 'amount_with_space_separator': return num(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return num(0, ' ', '.');
        default: return '';
      }
    });
  }

  /* ------------------------------------------------------- data enrichment */

  /*
   * /products/<handle>.js does NOT expose metafields, so the `jk` facts are
   * recovered from two places, in order:
   *   1. an inline [data-scent-catalog] JSON block, which sections/scent-finder
   *      builds straight from the metafields (present on the homepage), and
   *   2. the product's own tags — the fallback SPEC section 3 mandates
   *      (family:oud, gender:For Him, season:Winter, occasion:Office).
   */
  var catalogIndex = null;

  function getCatalogIndex() {
    if (catalogIndex) return catalogIndex;
    catalogIndex = {};
    var nodes = document.querySelectorAll('script[type="application/json"][data-scent-catalog]');
    for (var i = 0; i < nodes.length; i++) {
      try {
        var data = JSON.parse(nodes[i].textContent || '[]');
        if (!Array.isArray(data)) continue;
        for (var j = 0; j < data.length; j++) {
          if (data[j] && data[j].handle) catalogIndex[data[j].handle] = data[j];
        }
      } catch (e) { /* a malformed block must not break compare */ }
    }
    return catalogIndex;
  }

  /*
   * The scent-finder pills/swatches carry the canonical English value on
   * data-value and the *translated* label as their text (or aria-label), which
   * gives us a free EN -> current-locale map for gender / season / occasion /
   * family without hardcoding a single word here. Absent that section, the raw
   * metafield value is shown.
   */
  var labelMaps = null;

  function getLabelMaps() {
    if (labelMaps) return labelMaps;
    labelMaps = { gender: {}, season: {}, occasion: {}, family: {} };
    var opts = document.querySelectorAll('[data-scent-option]');
    for (var i = 0; i < opts.length; i++) {
      var field = attr(opts[i], 'data-scent-field');
      if (!labelMaps[field]) continue;
      var value = attr(opts[i], 'data-value');
      var label = attr(opts[i], 'aria-label') || (opts[i].textContent || '').trim();
      if (value && label) labelMaps[field][value.toLowerCase()] = label;
    }
    return labelMaps;
  }

  function localize(field, value) {
    if (!value) return '';
    var map = getLabelMaps()[field];
    var hit = map && map[String(value).toLowerCase()];
    return hit || value;
  }

  function tagValue(tags, prefix) {
    var list = asList(tags);
    for (var i = 0; i < list.length; i++) {
      var tag = String(list[i]);
      if (tag.toLowerCase().indexOf(prefix) === 0) return tag.slice(prefix.length).trim();
    }
    return '';
  }

  function tagValues(tags, prefix) {
    var out = [];
    var list = asList(tags);
    for (var i = 0; i < list.length; i++) {
      var tag = String(list[i]);
      if (tag.toLowerCase().indexOf(prefix) === 0) out.push(tag.slice(prefix.length).trim());
    }
    return out;
  }

  var productCache = {};

  function fetchProduct(handle) {
    if (Object.prototype.hasOwnProperty.call(productCache, handle)) {
      return Promise.resolve(productCache[handle]);
    }
    return fetch(rootPath() + '/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('not found');
        return response.json();
      })
      .catch(function () {
        return null;
      })
      .then(function (data) {
        productCache[handle] = data;
        return data;
      });
  }

  function facts(entry) {
    var cat = getCatalogIndex()[entry.handle] || {};
    var product = productCache[entry.handle] || {};
    var tags = product.tags;

    var season = asList(cat.season);
    if (!season.length) season = tagValues(tags, 'season:');

    var occasion = asList(cat.occasion);
    if (!occasion.length) occasion = tagValues(tags, 'occasion:');

    var price = cat.priceFormatted || '';
    if (!price && typeof product.price === 'number') price = formatMoney(product.price);

    var image = cat.image || '';
    if (!image && product.featured_image) image = product.featured_image;

    return {
      handle: entry.handle,
      title: cat.title || product.title || entry.title || entry.handle,
      image: image,
      gender: cat.gender || tagValue(tags, 'gender:'),
      family: cat.family || tagValue(tags, 'family:'),
      season: season,
      occasion: occasion,
      price: price,
      note: cat.note || ''
    };
  }

  /* ------------------------------------------------------------- artwork */

  function bottleMarkup() {
    var tpl = document.querySelector('template[data-scent-bottle]');
    if (tpl) return tpl.innerHTML;
    var svg = document.querySelector('.product-card__media-spin svg');
    return svg ? svg.outerHTML : '';
  }

  /* --------------------------------------------------------------- state */

  var list = [];

  function load() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      var data = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(data)) return [];
      var clean = [];
      for (var i = 0; i < data.length && clean.length < MAX; i++) {
        if (data[i] && typeof data[i].handle === 'string' && data[i].handle) {
          clean.push({ handle: data[i].handle, title: String(data[i].title || data[i].handle) });
        }
      }
      return clean;
    } catch (e) {
      return [];
    }
  }

  function save() {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) { /* private mode — the tray simply stops surviving navigation */ }
  }

  function indexOf(handle) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle) return i;
    }
    return -1;
  }

  /* ----------------------------------------------------------- elements */

  var bar, barItems, openBtn, countEl, notice;
  var overlay, headRow, tableBody, table;
  var modalOpen = false;
  var compareOpener = null;
  var noticeTimer = null;

  function cacheElements() {
    bar = document.querySelector('[data-compare-bar]');
    barItems = document.querySelector('[data-compare-bar-items]');
    openBtn = document.querySelector('[data-compare-open]');
    countEl = document.querySelector('[data-compare-count]');
    notice = document.querySelector('[data-compare-notice]');
    overlay = document.querySelector('[data-compare-modal-overlay]');
    table = document.querySelector('[data-compare-table]');
    headRow = document.querySelector('[data-compare-head-row]');
    tableBody = document.querySelector('[data-compare-body]');
  }

  /* ---------------------------------------------------------------- bar */

  function renderBar() {
    if (!bar) return;

    // JSX 1381: the bar does not exist at all while nothing is selected.
    bar.style.display = list.length ? '' : 'none';

    if (barItems) {
      var template = attr(bar, 'data-remove-label');
      var html = '';
      for (var i = 0; i < list.length; i++) {
        var title = list[i].title;
        var label = template ? template.split('__NAME__').join(title) : title;
        html +=
          '<span class="compare-bar__chip">' + esc(title) +
          '<button type="button" data-compare-remove="' + esc(list[i].handle) +
          '" aria-label="' + esc(label) + '">&times;</button></span>';
      }
      barItems.innerHTML = html;
    }

    if (openBtn) openBtn.disabled = list.length < MIN_TO_OPEN;
    // JSX 1395: the count is only appended once there is more than one.
    if (countEl) countEl.textContent = list.length > 1 ? '(' + list.length + ')' : '';
  }

  function syncToggles() {
    var buttons = document.querySelectorAll('[data-compare-toggle]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var on = indexOf(attr(btn, 'data-product-handle')) !== -1;
      // JSX 819 + CSS .product-card__quick-add button.is-comparing
      if (on) {
        btn.classList.add('is-comparing');
      } else {
        btn.classList.remove('is-comparing');
      }
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var label = attr(btn, on ? 'data-label-comparing' : 'data-label-choose');
      if (label) btn.textContent = label;
    }
  }

  function showNotice() {
    if (!notice) return;
    notice.classList.add('show');
    window.clearTimeout(noticeTimer);
    noticeTimer = window.setTimeout(function () {
      notice.classList.remove('show');
    }, NOTICE_MS);
  }

  /* -------------------------------------------------------------- modal */

  function cellValue(row, data) {
    switch (row) {
      case 'gender': return localize('gender', data.gender);
      case 'family': return localize('family', data.family);
      case 'season': return joinList(data.season.map(function (v) { return localize('season', v); }));
      case 'occasion': return joinList(data.occasion.map(function (v) { return localize('occasion', v); }));
      case 'price': return data.price;
      case 'note': return data.note;
      default: return '';
    }
  }

  function renderModal() {
    if (!overlay || !headRow || !tableBody) return;

    var empty = attr(table, 'data-label-empty') || '—';
    var removeLabel = attr(table, 'data-label-remove');
    var rows = ['gender', 'family', 'season', 'occasion', 'price', 'note']; // JSX 1404-1409 + 1441
    var data = list.map(facts);

    // Head: keep the leading label column, rebuild one <th> per fragrance.
    while (headRow.children.length > 1) {
      headRow.removeChild(headRow.lastElementChild);
    }
    var headHtml = '';
    for (var i = 0; i < data.length; i++) {
      var art = data[i].image
        ? '<img src="' + esc(data[i].image) + '" alt="' + esc(data[i].title) +
          '" loading="lazy" width="48" height="62" style="width:100%;">'
        : bottleMarkup();
      headHtml +=
        '<th><div class="compare-table__bottle">' + art + '</div>' +
        '<div class="compare-table__name">' + esc(data[i].title) + '</div>' +
        '<button type="button" class="compare-table__remove" data-compare-remove="' +
        esc(data[i].handle) + '">' + esc(removeLabel) + '</button></th>';
    }
    headRow.insertAdjacentHTML('beforeend', headHtml);

    var bodyHtml = '';
    for (var r = 0; r < rows.length; r++) {
      var row = rows[r];
      var label = attr(table, 'data-label-' + row);
      var noteClass = row === 'note' ? ' class="compare-table__note"' : '';
      bodyHtml += '<tr><td class="compare-table__label-col">' + esc(label) + '</td>';
      for (var c = 0; c < data.length; c++) {
        var value = cellValue(row, data[c]);
        bodyHtml += '<td' + noteClass + '>' + esc(value || empty) + '</td>';
      }
      bodyHtml += '</tr>';
    }
    tableBody.innerHTML = bodyHtml;
  }

  function openModal() {
    if (!overlay || list.length < MIN_TO_OPEN) return;
    // Re-entry must not stack a second scroll lock or lose the real opener.
    var wasOpen = modalOpen;
    if (!wasOpen) compareOpener = document.activeElement;
    modalOpen = true;
    overlay.style.display = '';
    renderModal();

    // Fill in whatever the inline catalog could not supply.
    Promise.all(list.map(function (entry) { return fetchProduct(entry.handle); }))
      .then(function () {
        if (modalOpen) renderModal();
      });

    var close = overlay.querySelector('[data-compare-close]');
    if (close) {
      try { close.focus({ preventScroll: true }); } catch (e) { close.focus(); }
    }

    // Same trap + scroll lock the cart drawer and the index panel use, so Tab
    // cannot walk out of a dialog that covers the whole viewport.
    if (!wasOpen) {
      if (window.JK && window.JK.lockScroll) window.JK.lockScroll();
      if (window.JK && window.JK.trapFocus) {
        window.JK.trapFocus(overlay.querySelector('[data-compare-modal]') || overlay, compareOpener);
      }
    }
  }

  function closeModal() {
    if (!overlay) return;
    var wasOpen = modalOpen;
    modalOpen = false;
    overlay.style.display = 'none';
    // Only unwind what this modal actually took: clear() reaches closeModal()
    // from the always-visible bar, and releasing a lock/trap owned by the cart
    // drawer would unlock the body and steal focus out from under it.
    if (!wasOpen) return;
    if (window.JK && window.JK.unlockScroll) window.JK.unlockScroll();
    if (window.JK && window.JK.releaseFocus) window.JK.releaseFocus(true);
    compareOpener = null;
  }

  /* -------------------------------------------------------------- actions */

  // JSX 2496-2507
  function toggle(handle, title) {
    if (!handle) return;
    var at = indexOf(handle);
    if (at !== -1) {
      list.splice(at, 1);
    } else if (list.length >= MAX) {
      showNotice();
      return; // the pick is ignored, not an error
    } else {
      list.push({ handle: handle, title: title || handle });
    }
    commit();
  }

  function remove(handle) {
    var at = indexOf(handle);
    if (at === -1) return;
    list.splice(at, 1);
    commit();
  }

  function clear() {
    // JSX 2510: clearing also dismisses the modal.
    list = [];
    closeModal();
    commit();
  }

  function commit() {
    save();
    renderBar();
    syncToggles();
    if (modalOpen) {
      // The React modal could linger over an empty table; on a real storefront
      // an empty comparison is just a dead overlay, so it closes instead.
      if (!list.length) closeModal();
      else renderModal();
    }
  }

  /* ----------------------------------------------------------------- wire */

  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var toggleBtn = target.closest('[data-compare-toggle]');
    if (toggleBtn) {
      event.preventDefault();
      toggle(attr(toggleBtn, 'data-product-handle'), attr(toggleBtn, 'data-product-title'));
      return;
    }

    var removeBtn = target.closest('[data-compare-remove]');
    if (removeBtn) {
      event.preventDefault();
      remove(attr(removeBtn, 'data-compare-remove'));
      return;
    }

    if (target.closest('[data-compare-clear]')) {
      event.preventDefault();
      clear();
      return;
    }

    if (target.closest('[data-compare-open]')) {
      event.preventDefault();
      openModal();
      return;
    }

    if (target.closest('[data-compare-close]')) {
      event.preventDefault();
      closeModal();
      return;
    }

    // JSX 1411: a click on the backdrop dismisses; JSX 1412 stops it inside.
    if (overlay && target === overlay) {
      closeModal();
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape' && modalOpen) closeModal();
  }

  // Idempotent on purpose: it must never slam a modal the shopper just opened,
  // so it only re-reads the DOM and repaints. The overlay ships hidden from
  // Liquid, so there is nothing here to close.
  function boot() {
    cacheElements();
    list = load();
    renderBar();
    syncToggles();
  }

  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);

  // scent-finder.js injects a product card at runtime; this lets its compare
  // button pick up the current selection state.
  document.addEventListener('jk:compare:sync', function () {
    syncToggles();
  });

  // Theme editor re-renders a section: re-find the nodes and repaint.
  document.addEventListener('shopify:section:load', function () {
    cacheElements();
    labelMaps = null;
    catalogIndex = null;
    renderBar();
    syncToggles();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
